import { Addresses, NameType, PersonInfoApi } from "../../api/Person";
import { ReportSummaryItemApi } from "../../api/Reports";


/** Format DOB as MM/DD/YYYY. Uses UTC methods to avoid timezone shift for ISO date-only strings. */
export function formatBirthDate(
  birthDate: string | undefined | null
): string | undefined {
  if (birthDate == null || birthDate === "") return undefined;
  try {
    const d = new Date(birthDate);
    if (Number.isNaN(d.getTime())) return undefined;
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    const year = d.getUTCFullYear();
    return `${month}/${day}/${year}`;
  } catch {
    return birthDate;
  }
}

/** Uses UTC methods for birthDate to avoid timezone shift for ISO date-only strings. */
export function getAge(
  birthDate: string | undefined | null
): number | undefined {
  if (birthDate == null || birthDate === "") return undefined;
  try {
    const d = new Date(birthDate);
    if (Number.isNaN(d.getTime())) return undefined;
    const today = new Date();
    let age = today.getFullYear() - d.getUTCFullYear();
    const m = today.getMonth() - d.getUTCMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getUTCDate())) age -= 1;
    return age >= 0 ? age : undefined;
  } catch {
    return undefined;
  }
}

export function formatAddress(addr: Addresses): string {
  const parts = [
    addr.line1,
    addr.line2,
    [addr.city, addr.state, addr.zip].filter(Boolean).join(" "),
    addr.country,
  ].filter(Boolean);
  return parts.join(" ").trim();
}

/** Screening company: parent only = company name; subcompany = [PARENT] | [SUB]. */
export function getDisplayCompanyName(data: ReportSummaryItemApi): string {
  const hasParent =
    data.parent_company_name &&
    data.parent_company_uuid &&
    data.parent_company_uuid !== data.company_uuid;
  if (hasParent) {
    return `${data.parent_company_name} | ${data.company_name}`;
  }
  return data.company_name || "";
}

/**
 * Parse date string to YYYY-MM-DD for input[type=date].
 * Returns "" for null, empty, or unparseable values so date inputs never receive invalid strings
 * (e.g. "ASDFASDF" or other string-backed date fields from the API).
 */
export function toDateInputValue(dateValue: string | undefined | null): string {
  if (dateValue == null || dateValue === "") return "";
  const trimmed = String(dateValue).trim();
  if (trimmed === "") return "";
  try {
    const d = new Date(trimmed);
    if (Number.isNaN(d.getTime())) return "";
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  } catch {
    return "";
  }
}

export function isValidBirthDate(val: string): boolean {
  if (!val || val.trim() === "") return true;
  const d = new Date(val);
  return !Number.isNaN(d.getTime()) && d.getTime() <= Date.now();
}

/** Valid date string (YYYY-MM-DD or parseable). Empty is valid. */
export function isValidDate(val: string): boolean {
  if (!val || val.trim() === "") return true;
  const d = new Date(val);
  return !Number.isNaN(d.getTime());
}

/** Start and end date range: both valid and start <= end. Empty dates are valid. */
export function isValidDateRange(start: string, end: string): boolean {
  if (!isValidDate(start) || !isValidDate(end)) return false;
  if (!start.trim() || !end.trim()) return true;
  return new Date(start) <= new Date(end);
}

export type PersonEditFormState = {
  primary: { firstName: string; middleName: string; lastName: string };
  aliases: {
    key: string;
    firstName: string;
    middleName: string;
    lastName: string;
  }[];
  emails: { key: string; email: string }[];
  phones: { key: string; phone: string }[];
  birthDate: string;
};

/** Convert PersonEditFormState to PersonInfoApi shape for optimistic update. Preserves jobs, addresses, degrees, identifications from base. */
export function formStateToPersonInfoApi(
  formState: PersonEditFormState,
  base: PersonInfoApi
): PersonInfoApi {
  const primaryName = {
    first_name: formState.primary.firstName.trim(),
    middle_name: formState.primary.middleName.trim() || undefined,
    last_name: formState.primary.lastName.trim(),
    name_type: NameType.MAIN as const,
  };
  const aliasNames = formState.aliases
    .filter((a) => a.firstName.trim() || a.lastName.trim())
    .map((a) => ({
      first_name: a.firstName.trim(),
      middle_name: a.middleName.trim() || undefined,
      last_name: a.lastName.trim(),
      name_type: NameType.ALIAS as const,
    }));
  const emails = formState.emails
    .filter((e) => e.email.trim() !== "")
    .map((e) => ({ address: e.email.trim() }));
  const phones = formState.phones
    .filter((p) => p.phone.trim() !== "")
    .map((p) => ({ raw_number: p.phone.trim() }));
  const birthDate =
    formState.birthDate && formState.birthDate.trim()
      ? new Date(formState.birthDate + "T12:00:00Z").toISOString().split("T")[0]
      : undefined;

  return {
    ...base,
    names: [primaryName, ...aliasNames],
    emails,
    phones,
    birth_date: birthDate,
  };
}
